import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ApiService {
    private baseURL: string;

    constructor() {
        this.baseURL = API_BASE_URL;
        this.setupInterceptors();
    }

    private setupInterceptors() {
        // Request interceptor to add auth token
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('authToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem('authToken');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    // Authentication
    async login(email: string, password: string) {
        const response = await axios.post(`${this.baseURL}/auth/login`, {
            email,
            password,
        });
        return response.data;
    }

    // Meeting Bot Methods (deprecated - using Teams integration instead)
    async joinMeetingBot(meetingId: string, consultationId: string, language: string) {
        console.warn('joinMeetingBot is deprecated - use Teams Integration instead');
        return { success: false, message: 'Use Teams Integration' };
    }

    async leaveMeetingBot(meetingId: string) {
        console.warn('leaveMeetingBot is deprecated - use Teams Integration instead');
        return { success: false, message: 'Use Teams Integration' };
    }

    async logout() {
        const response = await axios.post(`${this.baseURL}/auth/logout`);
        return response.data;
    }

    async verifyToken(token: string) {
        const response = await axios.post(`${this.baseURL}/auth/verify`, { token });
        return response.data;
    }

    // Dashboard metrics
    async getDashboardMetrics() {
        try {
            const response = await axios.get(`${this.baseURL}/dashboard/metrics`);
            return response.data;
        } catch (error) {
            console.error('Error fetching dashboard metrics:', error);
            // Return fallback data
            return {
                todaysConsultations: { completed: 24, total: 32 },
                activeNow: 6,
                pendingAssignments: 8,
                doctorAvailability: { available: 12, total: 18 },
                emergencyCalls: 1,
            };
        }
    }

    // Patients
    async getPatients() {
        try {
            const response = await axios.get(`${this.baseURL}/epic/patients`);
            return response.data;
        } catch (error) {
            console.error('Error fetching patients:', error);
            // Return fallback data
            return [
                {
                    id: '1',
                    name: 'Maria Gonzalez',
                    mrn: 'MRN-789123',
                    language: 'Spanish',
                    assignedDoctor: 'Dr. Sarah Johnson',
                    status: 'in progress',
                    consultationType: 'Crisis Intervention',
                },
                {
                    id: '2',
                    name: 'James Wilson',
                    mrn: 'MRN-456789',
                    language: 'English',
                    assignedDoctor: 'Dr. Michael Chen',
                    status: 'scheduled',
                    consultationType: 'Follow-up Assessment',
                },
                {
                    id: '3',
                    name: 'Sophie Dubois',
                    mrn: 'MRN-321654',
                    language: 'French',
                    assignedDoctor: 'Dr. Emily Rodriguez',
                    status: 'urgent',
                    consultationType: 'Emergency Assessment',
                },
                {
                    id: '4',
                    name: 'Ahmed Hassan',
                    mrn: 'MRN-654321',
                    language: 'Arabic',
                    assignedDoctor: 'Dr. Michael Chen',
                    status: 'routine',
                    consultationType: 'Initial Consultation',
                },
                {
                    id: '5',
                    name: 'Jennifer Martinez',
                    mrn: 'MRN-987654',
                    language: 'English',
                    assignedDoctor: 'Dr. Sarah Johnson',
                    status: 'routine',
                    consultationType: 'Follow-up Assessment',
                },
            ];
        }
    }

    // Schedule Management
    async getDoctorAvailability(date: string) {
        try {
            const response = await axios.get(`${this.baseURL}/schedule/doctors/availability`, {
                params: { date },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching doctor availability:', error);
            // Return fallback data
            return [
                {
                    id: '1',
                    firstName: 'Dr. Sarah',
                    lastName: 'Johnson',
                    specialty: 'Child Psychiatry',
                    isAvailable: true,
                    currentConsults: 2,
                    maxConsults: 6,
                    shift: 'morning',
                },
                {
                    id: '2',
                    firstName: 'Dr. Michael',
                    lastName: 'Chen',
                    specialty: 'Adult Psychiatry',
                    isAvailable: true,
                    currentConsults: 1,
                    maxConsults: 5,
                    shift: 'afternoon',
                },
                {
                    id: '3',
                    firstName: 'Dr. Emily',
                    lastName: 'Rodriguez',
                    specialty: 'Emergency Psychiatry',
                    isAvailable: false,
                    currentConsults: 4,
                    maxConsults: 4,
                    shift: 'evening',
                },
            ];
        }
    }

    async getConsultRequests(date: string) {
        try {
            const response = await axios.get(`${this.baseURL}/schedule/consult-requests`, {
                params: { date },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching consult requests:', error);
            // Return fallback data
            return [
                {
                    id: '1',
                    patientName: 'Maria Gonzalez',
                    patientId: 'P001',
                    mrn: 'MRN-789123',
                    priority: 'urgent',
                    consultationType: 'Crisis Intervention',
                    requestedTime: '09:00',
                    language: 'Spanish',
                    interpreterRequired: true,
                    status: 'unassigned',
                    epicEncounterId: 'ENC-001',
                },
                {
                    id: '2',
                    patientName: 'James Wilson',
                    patientId: 'P002',
                    mrn: 'MRN-456789',
                    priority: 'standard',
                    consultationType: 'Follow-up Assessment',
                    requestedTime: '10:30',
                    language: 'English',
                    interpreterRequired: false,
                    status: 'unassigned',
                    epicEncounterId: 'ENC-002',
                },
                {
                    id: '3',
                    patientName: 'Sophie Dubois',
                    patientId: 'P003',
                    mrn: 'MRN-321654',
                    priority: 'routine',
                    consultationType: 'Initial Consultation',
                    requestedTime: '14:00',
                    language: 'French',
                    interpreterRequired: true,
                    status: 'unassigned',
                    epicEncounterId: 'ENC-003',
                },
            ];
        }
    }

    async assignConsult(consultId: string, doctorId: string) {
        try {
            const response = await axios.post(`${this.baseURL}/schedule/assign-consult`, {
                consultId,
                doctorId,
            });
            return response.data;
        } catch (error) {
            console.error('Error assigning consult:', error);
            throw error;
        }
    }

    async createConsultRequest(consultData: any) {
        try {
            const response = await axios.post(
                `${this.baseURL}/schedule/consult-requests`,
                consultData
            );
            return response.data;
        } catch (error) {
            console.error('Error creating consult request:', error);
            throw error;
        }
    }

    async updateConsultStatus(consultId: string, status: string) {
        try {
            const response = await axios.patch(
                `${this.baseURL}/schedule/consult-requests/${consultId}/status`,
                {
                    status,
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating consult status:', error);
            throw error;
        }
    }

    // Doctor Dashboard
    async getDoctorSchedule(doctorId: string, date: string) {
        try {
            const response = await axios.get(
                `${this.baseURL}/schedule/doctors/${doctorId}/schedule`,
                {
                    params: { date },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching doctor schedule:', error);
            throw error;
        }
    }

    // Removed duplicate - using new implementation below

    // Voice recording and transcription
    async uploadAudio(audioBlob: Blob, consultationId?: string) {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');
        if (consultationId) {
            formData.append('consultationId', consultationId);
        }

        const response = await axios.post(`${this.baseURL}/audio/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async getTranscription(audioId: string) {
        const response = await axios.get(`${this.baseURL}/audio/transcription/${audioId}`);
        return response.data;
    }

    // Teams calling
    async initiateCall(participantIds: string[], consultationId?: string) {
        const response = await axios.post(`${this.baseURL}/teams/call/initiate`, {
            participantIds,
            consultationId,
        });
        return response.data;
    }

    async endCall(callId: string) {
        const response = await axios.post(`${this.baseURL}/teams/call/end`, { callId });
        return response.data;
    }

    // Consent management
    async storeConsentData(consultationId: string, consentData: any) {
        const response = await axios.post(
            `${this.baseURL}/consultations/${consultationId}/consent`,
            {
                consentData,
            }
        );
        return response.data;
    }

    // Epic EHR integration
    async getPatientData(patientId: string) {
        const response = await axios.get(`${this.baseURL}/epic/patients/${patientId}`);
        return response.data;
    }

    async submitClinicalNote(encounterId: string, note: string) {
        const response = await axios.post(`${this.baseURL}/epic/clinical-notes`, {
            encounterId,
            note,
        });
        return response.data;
    }

    // AI Notes generation
    async generateClinicalNote(
        consultationId: string,
        transcription: string,
        consultationContext: any
    ) {
        const response = await axios.post(`${this.baseURL}/ai-notes/generate`, {
            consultationId,
            transcription,
            consultationContext,
        });
        return response.data;
    }

    async saveDraftNote(consultationId: string, draftNote: any, metadata: any = {}) {
        const response = await axios.post(`${this.baseURL}/ai-notes/${consultationId}/draft`, {
            draftNote,
            metadata,
        });
        return response.data;
    }

    async submitFinalNote(consultationId: string, finalNote: any, submissionMetadata: any = {}) {
        const response = await axios.post(`${this.baseURL}/ai-notes/${consultationId}/submit`, {
            finalNote,
            submissionMetadata,
        });
        return response.data;
    }

    async processTranscriptionChunk(consultationId: string, transcriptionChunk: string) {
        const response = await axios.post(`${this.baseURL}/ai-notes/transcription/process`, {
            consultationId,
            transcriptionChunk,
        });
        return response.data;
    }

    async getNotesHistory(consultationId: string) {
        const response = await axios.get(`${this.baseURL}/ai-notes/${consultationId}/history`);
        return response.data;
    }

    // Billing management
    async generateBilling(consultationId: string, insuranceType?: string, patientInsurance?: any) {
        const response = await axios.post(`${this.baseURL}/billing/generate`, {
            consultationId,
            insuranceType,
            patientInsurance,
        });
        return response.data;
    }

    async reviewBilling(consultationId: string, billingData: any, reviewNotes?: string) {
        const response = await axios.put(`${this.baseURL}/billing/${consultationId}/review`, {
            billingData,
            reviewNotes,
        });
        return response.data;
    }

    async approveBilling(consultationId: string, finalBillingData: any, approvalNotes?: string) {
        const response = await axios.post(`${this.baseURL}/billing/${consultationId}/approve`, {
            finalBillingData,
            approvalNotes,
        });
        return response.data;
    }

    async rejectBilling(consultationId: string, rejectionReason: string) {
        const response = await axios.post(`${this.baseURL}/billing/${consultationId}/reject`, {
            rejectionReason,
        });
        return response.data;
    }

    async getBillingHistory(consultationId: string) {
        const response = await axios.get(`${this.baseURL}/billing/${consultationId}/history`);
        return response.data;
    }

    async getBillingSummary(
        doctorId?: string,
        dateFrom?: string,
        dateTo?: string,
        status?: string
    ) {
        const params = new URLSearchParams();
        if (doctorId) params.append('doctorId', doctorId);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        if (status) params.append('status', status);

        const response = await axios.get(`${this.baseURL}/billing/summary?${params.toString()}`);
        return response.data;
    }

    // Analytics endpoints
    async getAnalyticsDashboard(timeframe: string = 'month') {
        const response = await axios.get(`${this.baseURL}/analytics/dashboard`, {
            params: { timeframe },
        });
        return response.data.data;
    }

    async getConsultationMetrics(timeframe: string = 'month') {
        const response = await axios.get(`${this.baseURL}/analytics/consultations`, {
            params: { timeframe },
        });
        return response.data.data;
    }

    async getDoctorUtilization(timeframe: string = 'month') {
        const response = await axios.get(`${this.baseURL}/analytics/doctors`, {
            params: { timeframe },
        });
        return response.data.data;
    }

    async getRealTimeMetrics() {
        const response = await axios.get(`${this.baseURL}/analytics/realtime`);
        return response.data.data;
    }

    async exportAnalytics(timeframe: string = 'month', format: 'json' | 'csv' = 'json') {
        const response = await axios.get(`${this.baseURL}/analytics/export`, {
            params: { timeframe, format },
            responseType: 'blob',
        });

        // Handle file download
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-${timeframe}-${Date.now()}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    // AI Scheduling endpoints
    async getAIRecommendations(consultationRequest: any) {
        const response = await axios.post(
            `${this.baseURL}/ai-scheduling/recommendations`,
            consultationRequest
        );
        return response.data.data;
    }

    async analyzeCaseComplexity(caseData: any) {
        const response = await axios.post(`${this.baseURL}/ai-scheduling/case-analysis`, caseData);
        return response.data.data;
    }

    async getWorkloadAnalysis(timeframe: string = 'today') {
        const response = await axios.get(`${this.baseURL}/ai-scheduling/workload-analysis`, {
            params: { timeframe },
        });
        return response.data.data;
    }

    async getDoctorWorkloadAnalysis(doctorId: string, timeframe: string = 'today') {
        const response = await axios.get(
            `${this.baseURL}/ai-scheduling/doctor-workload/${doctorId}`,
            {
                params: { timeframe },
            }
        );
        return response.data.data;
    }

    async getDoctorPerformanceReport(doctorId: string, timeframe: string = 'month') {
        const response = await axios.get(
            `${this.baseURL}/ai-scheduling/performance-report/${doctorId}`,
            {
                params: { timeframe },
            }
        );
        return response.data.data;
    }

    async optimizeAssignment(consultationId: string, forceReassignment: boolean = false) {
        const response = await axios.post(`${this.baseURL}/ai-scheduling/optimize-assignment`, {
            consultationId,
            forceReassignment,
        });
        return response.data.data;
    }

    async getCapacityForecast(days: number = 7) {
        const response = await axios.get(`${this.baseURL}/ai-scheduling/capacity-forecast`, {
            params: { days },
        });
        return response.data.data;
    }

    async getEmergencyAssignment(requestData: any) {
        const response = await axios.post(
            `${this.baseURL}/ai-scheduling/emergency-assignment`,
            requestData
        );
        return response.data.data;
    }

    async getSystemStatus() {
        const response = await axios.get(`${this.baseURL}/ai-scheduling/system-status`);
        return response.data.data;
    }

    // Doctor Preferences Management
    async getDoctorPreferences() {
        const response = await axios.get(`${this.baseURL}/doctor-preferences`);
        return response.data;
    }

    async updateDoctorPreferences(preferences: any) {
        const response = await axios.put(`${this.baseURL}/doctor-preferences`, preferences);
        return response.data;
    }

    async resetDoctorPreferences() {
        const response = await axios.post(`${this.baseURL}/doctor-preferences/reset`);
        return response.data;
    }

    async getDoctorAvailabilityRange(doctorId: string, startDate: string, endDate: string) {
        const response = await axios.get(
            `${this.baseURL}/doctor-preferences/availability/${doctorId}`,
            {
                params: { startDate, endDate },
            }
        );
        return response.data;
    }

    // =================== CONSULTATION MANAGEMENT ===================
    async createConsultation(consultationData: {
        patientId: string;
        doctorId: string;
        scheduledTime: string;
        consultationType: string;
        priority?: string;
        interpreterRequired?: boolean;
        interpreterLanguage?: string;
        estimatedDuration?: number;
        notes?: string;
    }) {
        const response = await axios.post(`${this.baseURL}/consultations`, consultationData);
        return response.data;
    }

    async startConsultation(consultationId: string) {
        const response = await axios.post(`${this.baseURL}/consultations/${consultationId}/start`);
        return response.data;
    }

    async startDeviceConsultation(consultationId: string, deviceId: string) {
        const response = await axios.post(
            `${this.baseURL}/consultations/${consultationId}/start-device`,
            { deviceId }
        );
        return response.data;
    }

    async endConsultation(
        consultationId: string,
        endData: {
            notes?: string;
            summary?: string;
        }
    ) {
        const response = await axios.post(
            `${this.baseURL}/consultations/${consultationId}/end`,
            endData
        );
        return response.data;
    }

    async rescheduleConsultation(
        consultationId: string,
        rescheduleData: {
            newStartTime: string;
            newDuration?: number;
            reason?: string;
        }
    ) {
        const response = await axios.put(
            `${this.baseURL}/consultations/${consultationId}/reschedule`,
            rescheduleData
        );
        return response.data;
    }

    async cancelConsultation(consultationId: string, reason?: string) {
        const response = await axios.delete(`${this.baseURL}/consultations/${consultationId}`, {
            data: { reason },
        });
        return response.data;
    }

    async getConsultation(consultationId: string) {
        const response = await axios.get(`${this.baseURL}/consultations/${consultationId}`);
        return response.data;
    }

    async getActiveConsultations() {
        const response = await axios.get(`${this.baseURL}/consultations/active/teams`);
        return response.data;
    }
}

export const apiService = new ApiService();
